import { CustomChip } from '@app/components/CustomChip';
import { usePlaybackCurrentTs, usePlaybackPlay } from '@app/core/playback/hooks/usePlayback';
import { pastelColors } from '@app/core/ui/colors';
import { FragmentChunk } from '@app/modules/fragments/types';
import { formatDuration } from '@mi-front/core';
import { Box } from '@mui/material';
import { useMemo } from 'react';
import { defineMessage, useIntl } from 'react-intl';
import {
  getActiveChunkIndex,
  getChunkPlaybackRangeTsFromDurations,
} from './audio-fragment-chunk-pills-utils';

const unlabeledChunkMessage = defineMessage({
  id: 'fragmentMedia.audio.chunkPill.unlabeledChunk',
  defaultMessage: 'Segmento {n}',
});

export type AudioFragmentChunkPillsProps = {
  chunks: FragmentChunk[];
  /** Total ms for width normalization (typically sum of `chunk.duration` values). */
  fragmentDurationMs: number;
};

/**
 * Full-width row of chips under the audio player: one per chunk (label + duration).
 * Each pill's width is proportional to `chunk.duration / fragmentDurationMs` (flex-grow weights).
 * If `fragmentDurationMs` is greater than the sum of chunk durations, an empty flex segment keeps
 * proportions aligned with that total. The chip whose range contains the playhead is filled (active).
 */
export function AudioFragmentChunksPills({
  chunks,
  fragmentDurationMs,
}: AudioFragmentChunkPillsProps) {
  const { $t } = useIntl();
  const currentTs = usePlaybackCurrentTs();
  const play = usePlaybackPlay();
  const activeIndex = getActiveChunkIndex(chunks, currentTs);

  const sumChunkMs = useMemo(() => chunks.reduce((sum, c) => sum + c.duration, 0), [chunks]);

  const remainderMs = useMemo(() => {
    if (fragmentDurationMs <= 0) {
      return 0;
    }
    return Math.max(0, fragmentDurationMs - sumChunkMs);
  }, [fragmentDurationMs, sumChunkMs]);

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        gap: 0.5,
        mt: 1.5,
        minWidth: 0,
      }}
    >
      {chunks.map((chunk, index) => {
        const isActive = activeIndex === index;

        const titleText = chunk.label?.trim()
          ? chunk.label
          : $t(unlabeledChunkMessage, { n: index + 1 });

        const durationText = formatDuration(chunk.duration);
        const label = `${titleText} · ${durationText}`;
        const chipColor = pastelColors[index % pastelColors.length];

        return (
          <CustomChip
            key={`${chunk.from}-${chunk.to}`}
            chipColor={chipColor}
            label={label}
            size="small"
            variant={isActive ? 'filled' : 'outlined'}
            onClick={() => {
              const range = getChunkPlaybackRangeTsFromDurations(chunks, index);
              if (!range) {
                return;
              }
              play({ startTs: range.startTs, stopTs: range.stopTs });
            }}
            sx={{
              typography: 'caption',
              cursor: 'pointer',
              flexGrow: chunk.duration,
              flexBasis: 0,
              flexShrink: 1,
              minWidth: 0,
              width: 0,
              maxWidth: '100%',
              justifyContent: 'center',
              '& .MuiChip-label': {
                fontWeight: isActive ? 500 : 400,
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
                paddingLeft: 1.5,
              },
            }}
          />
        );
      })}
      {remainderMs > 0 && (
        <Box
          sx={{
            flexGrow: remainderMs,
            flexBasis: 0,
            flexShrink: 1,
            minWidth: 0,
          }}
        />
      )}
    </Box>
  );
}
